import { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";

import {
  strCode,
  releaseNoteOptions as releaseNoteOptionNames,
  alignItems
} from "../../../../../consts";
import { t } from "../../../../../services";

import {
  DropdownInput,
  FlexContainers,
  IconButton,
  Paragraph2,
  RadioOptionsSelector,
  SVG,
  WrapperContainer
} from "../../../../../components";
import { FormSection } from "../../../../../templates";
import { ReleaseNoteOption } from "./ReleaseNoteOption";
import { ReleaseNotesOptionsInfo } from "./ReleaseNotesOptionsInfo";
import { LocalizationConfig } from "../LocalIzationConfig/LocalIzationConfig";
import {
  OptionComponent,
  ReleaseNotesFormSection,
  ReleaseNotesSelectorWrapper,
  RichReleaseNotesDropdownContainer
} from "./styled";

export const ReleaseNotes = ({
  releaseNoteOption,
  loading,
  localizations,
  iconsData,
  thumbnailsData,
  missingFields,
  showErrors,
  setLocalizations,
  setIconsData,
  setThumbnailsData,
  onChangeOption
}) => {
  const [selectedOption, setSelectedOption] = useState();

  const richReleaseNotesOptions = useMemo(() => {
    return [
      {
        id: releaseNoteOptionNames.LIST_WITH_ICONS,
        value: strCode.LIST_WITH_ICONS_TITLE,
        component: (
          <OptionComponent
            isSelected={selectedOption?.id === releaseNoteOptionNames.LIST_WITH_ICONS}
            Container={FlexContainers.FlexRowStart}
            gap={4}
            width={"100%"}>
            <SVG.ListWithIconsOptionIcon />
            <Paragraph2>{t(strCode.LIST_WITH_ICONS_TITLE)}</Paragraph2>
          </OptionComponent>
        )
      },
      {
        id: releaseNoteOptionNames.WALKTHROUGH,
        value: strCode.CAROUSEL_TITLE,
        component: (
          <OptionComponent
            isSelected={selectedOption?.id === releaseNoteOptionNames.WALKTHROUGH}
            Container={FlexContainers.FlexRowStart}
            gap={4}
            width={"100%"}>
            <SVG.WalkthroughOptionIcon />
            <Paragraph2>{t(strCode.CAROUSEL_TITLE)}</Paragraph2>
          </OptionComponent>
        )
      }
    ];
  }, [selectedOption?.id]);

  const handleSelectRichReleaseNotesOption = (id) => {
    onChangeOption(id);
    setSelectedOption(richReleaseNotesOptions.find((el) => el.id === id));
  };

  useEffect(() => {
    setSelectedOption(richReleaseNotesOptions[0]);
  }, []);

  useEffect(() => {
    if (releaseNoteOption !== releaseNoteOptionNames.SIMPLE_LIST) {
      handleSelectRichReleaseNotesOption(releaseNoteOption);
    }
  }, [releaseNoteOption]);

  useEffect(() => {
    if (releaseNoteOption !== releaseNoteOptionNames.SIMPLE_LIST && selectedOption) {
      handleSelectRichReleaseNotesOption(selectedOption?.id);
    }
  }, [selectedOption]);

  const RichReleaseNotesDropdown = (
    <RichReleaseNotesDropdownContainer width={220}>
      <DropdownInput
        id={"rich-release-notes-dropdown"}
        options={richReleaseNotesOptions}
        selectedOption={selectedOption}
        onSelectOption={handleSelectRichReleaseNotesOption}
        InputComponent={selectedOption?.component ?? null}
        optionsListWidth={"220"}
      />
    </RichReleaseNotesDropdownContainer>
  );

  const releaseNoteOptions = [
    {
      id: releaseNoteOptionNames.SIMPLE_LIST,
      Component: (
        <ReleaseNoteOption
          title={strCode.SIMLE_LIST_TITLE}
          icon={<SVG.SimpleListOptionIcon />}
          isActive={releaseNoteOption === releaseNoteOptionNames.SIMPLE_LIST}
        />
      )
    },
    {
      id: selectedOption?.id ?? releaseNoteOptionNames.LIST_WITH_ICONS,
      Component: (
        <ReleaseNoteOption
          title={strCode.RICH_RELEASE_NOTES_TITLE}
          isActive={[
            releaseNoteOptionNames.LIST_WITH_ICONS,
            releaseNoteOptionNames.WALKTHROUGH
          ].includes(releaseNoteOption)}
          Component={RichReleaseNotesDropdown}
        />
      )
    }
  ];

  return (
    <ReleaseNotesFormSection width={"100%"}>
      <FormSection title={t(strCode.RELEASE_NOTES_TITLE)} infoText={t(strCode.OPTIONAL_INFO)}>
        <WrapperContainer
          Container={FlexContainers.FlexColumnStart}
          alignItems={alignItems.start}
          width={"100%"}
          gap={16}>
          <ReleaseNotesSelectorWrapper
            Container={FlexContainers.FlexColumnStart}
            alignItems={alignItems.start}
            ph={24}
            width={"100%"}
            gap={4}>
            <WrapperContainer Container={FlexContainers.FlexRowStart} gap={8}>
              <Paragraph2>{t(strCode.RELEASE_NOTES_SLECTOR_LABEL)}</Paragraph2>
              <IconButton
                hoverIcon={<SVG.QuestionMarkIcon />}
                tooltipComponent={<ReleaseNotesOptionsInfo />}>
                <SVG.QuestionMarkOutlineIcon />
              </IconButton>
            </WrapperContainer>

            <RadioOptionsSelector
              name="release-notes-options"
              options={releaseNoteOptions}
              selectedId={releaseNoteOption}
              onChange={onChangeOption}
            />
          </ReleaseNotesSelectorWrapper>

          {!loading && (
            <LocalizationConfig
              releaseNoteOption={releaseNoteOption}
              languageList={localizations}
              iconsData={iconsData}
              thumbnailsData={thumbnailsData}
              setLanguageList={setLocalizations}
              setIconsData={setIconsData}
              setThumbnailsData={setThumbnailsData}
              missingFields={missingFields?.localizations}
              showErrors={showErrors}
            />
          )}
        </WrapperContainer>
      </FormSection>
    </ReleaseNotesFormSection>
  );
};

ReleaseNotes.propTypes = {
  releaseNoteOption: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  localizations: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number.isRequired, title: PropTypes.string.isRequired })
  ).isRequired,
  iconsData: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number.isRequired, icon: PropTypes.object })
  ),
  thumbnailsData: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number.isRequired, media: PropTypes.object })
  ),
  missingFields: PropTypes.arrayOf(
    PropTypes.shape({
      language: PropTypes.string.isRequired,
      newFeaturesList: PropTypes.string.isRequired,
      improvementsList: PropTypes.string.isRequired,
      showErrors: PropTypes.bool
    }).isRequired
  ),
  showErrors: PropTypes.bool.isRequired,
  setLocalizations: PropTypes.func.isRequired,
  setIconsData: PropTypes.func.isRequired,
  setThumbnailsData: PropTypes.func.isRequired,
  onChangeOption: PropTypes.func.isRequired
};
